import { PlantIntervalMark } from "api"
import { ScatterDataPoint } from "chart.js"

export enum FuelDataUnit {
	TONNES = "tonnes",
	CUBIC_METER = "cubicMeter",
}

export enum DataSource {
	SENSOR = "sensor",
	UNISEA = "unisea",
}

export type AccumulatedValues = {
	shipName: string
	values: (ResultItem & { fuelBreakdown?: FuelBreakdownValue[] })[]
	hasMultipleFuelTypes?: boolean
}

export enum STACK_PLOT_RANGE {
	YEAR = "year",
	QUARTER = "quarter",
	MONTH = "month",
	WEEK = "week",
	DAY = "day",
}

export enum SHOW_TOTAL {
	SHOW = "show",
	HIDE = "hide",
}

// Whether the Total Fuel Consumption chart (and its mode list) splits each op-mode/fuel-type's bar
// into a sensor-sourced portion and a UniSea-sourced portion (SHOW), or sums them into one merged
// figure (HIDE, the default) — e.g. sensor "Transit eco" and UniSea-backfilled "Transit eco" render
// as a single "Transit eco" entry when HIDE.
export enum SHOW_DATA_SOURCE {
	SHOW = "show",
	HIDE = "hide",
}

export type FuelBreakdownValue = {
	key: "mdo" | "gas" | "hfo" | "lvoc" | "mgo" | "svoc"
	label: string
	value: number
}

export enum DisplayValue {
	FUEL = "Fuel",
	CO2 = "CO2",
	NOX = "NOx",
	SOX = "SOx",
}

export interface ResultItem {
	value: number
	label: string
}

export interface StatisticsType {
	from: string
	to: string
	lossAverageSpeed: number
	speedAverage: number
	lossInterpolated: Statistics
}

export type Statistics = {
	lossMedian: number
	lossAverage: number
}

export type Coefficients = {
	a: number
	b: number
	c: number
}

export type InterpolatedGroupedType = {
	label: string
	avgSpeed: number
	loss: number
	data: (number | ScatterDataPoint | null)[]
	coeff: Coefficients
	xyData: ScatterDataPoint[]
}

export interface HeatmapGroupedType {
	label: string
	avgSpeed: number
	loss: number
	data: (number | ScatterDataPoint | null)[]
	coeff: Coefficients
	xyData: ScatterDataPoint[]
}

export interface CardData {
	text: string
	value: string
	color?: boolean
}

export enum HEAT_MAP_TYPES {
	"kgPerNm" = "kgPerNm",
	"propulsionPower" = "propulsionPower",
	"tonnesPerDay" = "tonnesPerDay",
	"trim" = "trim",
}

export interface LegendItem {
	label: string
	displayLabel?: string
	isHidden: boolean
	isForcedHidden?: boolean
	isForcedHiddenOverridden?: boolean
	userHidden?: boolean
}

export interface INTERVAL_MARKS {
	plants: { id: number; marks: PlantIntervalMark[] }[]
}

// Whether the "Metrics" KPI panel is showing UniSea-backfilled data alongside sensor data, or only
// sensor data — user-controlled via the panel's toggle, shared between the live view and the
// print/Excel report paths so a report always reflects the same choice the user made on screen.
export type MetricsDataCompletenessMode = "backfilled" | "sensorOnly"

export type MetricsPanelExportRow = {
	plantId: number
	vesselLabel: string
	metricKey:
		| "co2Saving"
		| "fuelSaving"
		| "cii"
		| "operationTime"
		| "avgKgPerNm"
		| "avgSpeed"
		| "accumulatedNm"
	metricLabel: string
	unit: string
	value: number | null
	deviationPercent: number | null
}

export type MetricsPanelExportData = {
	baselineYear: number
	isUniSea: boolean
	dataCompletenessMode: MetricsDataCompletenessMode
	dataCompletenessLabel: string | null
	rows: MetricsPanelExportRow[]
}
